/**
 * Access reviews and the examiner export (ADR-034).
 *
 * A review lists every live role holding for a keep-or-remove decision. A
 * reviewer never decides about their own role, or one they gave or approved —
 * where nobody else could, the decision is recorded as "independent review
 * unavailable" rather than passed off as independent.
 *
 * The export is every record of the app's roles here, one section at a time:
 * the server pages each section (it never sends one response it cannot
 * carry), and this downloads every page of a section into one JSON file. The
 * file says whether it holds the whole section (`complete`) and the row count
 * the summary gave (`rowsAtSummary`); a download that came back with fewer
 * rows than the summary, or stopped short, is flagged rather than passed off
 * as complete.
 */
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import {
  api,
  type AppAccessExportSectionDto,
  type AppAccessExportSummaryDto,
  type AppAccessScreenDto,
  type VaultHolderMemberDto,
} from '@/lib/api';
import {
  useAppAccessReview,
  useCompleteAppAccessReview,
  useDecideAppAccessReviewItem,
  useStartAppAccessReview,
  type AppAccessScope,
} from '../../hooks/useAppAccess';
import { describeAppAccessError, memberLabel, roleLabel } from './shared';

interface ReviewsSectionProps {
  scope: AppAccessScope;
  screen: AppAccessScreenDto;
  members: readonly VaultHolderMemberDto[];
}

/** The most pages one section download follows before giving up (a runaway guard). */
const MAX_EXPORT_PAGES = 1000;

const SECTION_LABELS: Record<AppAccessExportSectionDto, string> = {
  policies: 'Role and permission definitions (every release)',
  assignments: 'Role holdings, current and past',
  acceptances: 'Accepted permissions',
  proposals: 'Sensitive-role proposals',
  requests: 'Access requests',
  appointments: 'Access managers and reviewers',
  reviews: 'Reviews',
  reviewItems: 'Review decisions',
  workspaceState: 'When roles were turned on, and by whom',
  settings: 'Settings',
};

function saveJson(filename: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  // After the browser has taken the download: revoking at once can cancel it.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function ReviewsSection({ scope, screen, members }: ReviewsSectionProps) {
  const confirm = useConfirm();
  const review = useAppAccessReview(scope);
  const start = useStartAppAccessReview(scope);
  const decide = useDecideAppAccessReviewItem(scope);
  const complete = useCompleteAppAccessReview(scope);
  const [summary, setSummary] = useState<AppAccessExportSummaryDto | null>(null);
  const [downloading, setDownloading] = useState<AppAccessExportSectionDto | null>(null);
  const byPrincipal = new Map(members.map((m) => [m.principalId, m]));
  const name = (p: string) =>
    p === screen.seat.principalId ? 'You' : memberLabel(byPrincipal.get(p), p);
  const onError = (title: string) => (err: unknown) =>
    toast({ title, description: describeAppAccessError(err), variant: 'destructive' });

  async function loadSummary() {
    try {
      setSummary(await api.getAppAccessExportSummary(scope.holderId, scope.archeId));
    } catch (err) {
      onError('Export unavailable')(err);
    }
  }

  async function download(section: AppAccessExportSectionDto, expected: number) {
    setDownloading(section);
    try {
      const rows: Array<Record<string, unknown>> = [];
      let cursor: string | null = null;
      let pages = 0;
      do {
        const page = await api.getAppAccessExportPage(
          scope.holderId,
          scope.archeId,
          section,
          cursor,
        );
        rows.push(...page.rows);
        cursor = page.nextCursor;
        pages += 1;
      } while (cursor && pages < MAX_EXPORT_PAGES);
      saveJson(`app-access-${scope.archeId}-${section}.json`, {
        holderId: scope.holderId,
        archeId: scope.archeId,
        section,
        exportedAt: new Date().toISOString(),
        // Rows written between the summary and the download make these differ;
        // the file says so rather than hiding it.
        rowsAtSummary: expected,
        complete: cursor === null,
        rows,
      });
      if (cursor !== null) {
        toast({
          title: 'Export incomplete',
          description: 'This section is too large to download in one go. Contact support.',
          variant: 'destructive',
        });
      } else if (rows.length < expected) {
        // Rows can only be added, so fewer than the summary counted means
        // something was lost on the way — say so rather than hand it over.
        toast({
          title: 'Export may be incomplete',
          description: `The summary counted ${expected} rows; the file has ${rows.length}. Download it again.`,
          variant: 'destructive',
        });
      }
    } catch (err) {
      onError('Download failed')(err);
    } finally {
      setDownloading(null);
    }
  }

  const open = review.data;
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Access review</CardTitle>
          <CardDescription>
            Go through every role held here and keep or remove it. Nobody reviews their own role, or
            one they gave or approved, while someone else could.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {review.isLoading ? (
            <p className="text-muted-foreground">Loading…</p>
          ) : !open ? (
            <Button
              size="sm"
              disabled={start.isPending}
              onClick={() => start.mutate(undefined, { onError: onError('Review not started') })}
            >
              Start a review
            </Button>
          ) : (
            <>
              <p className="text-muted-foreground">
                Started by {name(open.startedByPrincipalId)} on{' '}
                {new Date(open.startedAt).toLocaleDateString()}.
              </p>
              {/* Self-grants first — no second pair of eyes ever looked at them
                  (the server's order; kept here for an older server's list). */}
              {[...open.items]
                .sort((a, b) => Number(b.selfGranted === true) - Number(a.selfGranted === true))
                .map((item) => (
                  <div
                    key={item.itemId}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-2"
                  >
                    <span>
                      <span className="font-medium">{name(item.principalId)}</span> —{' '}
                      {roleLabel(screen.access.roles, item.roleId)}
                      {item.selfGranted ? (
                        <Badge variant="destructive" className="ml-2">
                          Granted to themselves
                        </Badge>
                      ) : (
                        item.grantedByPrincipalId && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            granted by {name(item.grantedByPrincipalId)}
                          </span>
                        )
                      )}
                      {item.independentReviewUnavailable && (
                        <Badge variant="outline" className="ml-2">
                          Independent review unavailable
                        </Badge>
                      )}
                    </span>
                    {item.decision ? (
                      <span className="capitalize text-muted-foreground">
                        {item.decision === 'keep' ? 'Kept' : 'Removed'}
                      </span>
                    ) : (
                      <span className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={decide.isPending}
                          onClick={() =>
                            decide.mutate(
                              { reviewId: open.reviewId, itemId: item.itemId, decision: 'keep' },
                              { onError: onError('Not recorded') },
                            )
                          }
                        >
                          Keep
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={decide.isPending}
                          onClick={() =>
                            decide.mutate(
                              { reviewId: open.reviewId, itemId: item.itemId, decision: 'remove' },
                              { onError: onError('Not recorded') },
                            )
                          }
                        >
                          Remove
                        </Button>
                      </span>
                    )}
                  </div>
                ))}
              {(() => {
                const undecided = open.items.filter((i) => i.decision === null).length;
                return (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={complete.isPending}
                      onClick={async () => {
                        // A review completed with items undecided is recorded
                        // as such — say so before it happens.
                        if (
                          undecided > 0 &&
                          !(await confirm({
                            title: 'Complete the review anyway?',
                            description: `${undecided} ${undecided === 1 ? 'item is' : 'items are'} still undecided. It will be recorded as completed with ${undecided} undecided.`,
                            confirmLabel: 'Complete review',
                          }))
                        ) {
                          return;
                        }
                        complete.mutate(open.reviewId, {
                          onError: onError('Review not completed'),
                        });
                      }}
                    >
                      Complete review
                    </Button>
                    {undecided > 0 && (
                      <span className="text-muted-foreground">{undecided} undecided</span>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Export for an examiner</CardTitle>
          <CardDescription>
            Every record of who held which role here and every decision about it. Each download is
            recorded.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {!summary ? (
            <Button size="sm" variant="outline" onClick={loadSummary}>
              Prepare export
            </Button>
          ) : (
            summary.sections.map((s) => (
              <div key={s.section} className="flex items-center justify-between gap-2">
                <span>
                  {SECTION_LABELS[s.section]}{' '}
                  <span className="text-muted-foreground">({s.rows})</span>
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={downloading !== null}
                  onClick={() => download(s.section, s.rows)}
                >
                  {downloading === s.section ? 'Downloading…' : 'Download'}
                </Button>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
